import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Zap, Droplets, Construction, LayoutGrid, Wrench, ArrowRight } from "lucide-react";

const categories = [
  {
    icon: Zap,
    title: "Electrical",
    slug: "electrical",
    description:
      "Switches, wiring, panels, and industrial-grade electrical components from trusted manufacturers.",
  },
  {
    icon: Wrench,
    title: "Hardware",
    slug: "hardware",
    description:
      "Fasteners, tools, locks, hinges, and general hardware for construction and maintenance needs.",
  },
  {
    icon: Droplets,
    title: "Sanitary",
    slug: "sanitary",
    description:
      "Premium sanitary ware, fittings, and bathroom essentials built for durability and modern aesthetics.",
  },
  {
    icon: Construction,
    title: "Iron & Steel",
    slug: "iron-and-steel",
    description:
      "Structural steel, iron fittings, and fabrication materials sourced for strength and reliability.",
  },
  {
    icon: LayoutGrid,
    title: "Tiles",
    slug: "tiles",
    description:
      "Floor, wall, and decorative tiles in a wide range of finishes for residential and commercial projects.",
  },
];

export function ProductsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="products" className="py-32 bg-background relative">
      <div className="absolute top-0 left-0 w-full h-px bg-border" />

      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center mb-20"
        >
          <span className="inline-block text-xs tracking-wide-elegant uppercase text-muted-foreground mb-6">
            What We Deal In
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-8 leading-[1.1] font-medium">
            Product
            <br />
            <span className="text-muted-foreground">Categories</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            We specialise in sourcing and distributing quality products across
            five core categories — built for the demands of modern industry and
            infrastructure.
          </p>
        </motion.div>

        {/* Categories Grid — 5 items in a single row on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border sm:[&>*:last-child]:col-span-2 lg:[&>*:last-child]:col-span-1">
          {categories.map((cat, i) => (
            <motion.div
              key={cat.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
            >
              <Link
                to={`/${cat.slug}`}
                className="group bg-background p-8 lg:p-6 xl:p-8 h-full flex flex-col items-center text-center hover:bg-muted/40 transition-all duration-500 relative overflow-hidden"
                aria-label={`${cat.title} products — learn more`}
              >
                {/* Subtle top accent line on hover */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-px bg-foreground/40 group-hover:w-full transition-all duration-500" />

                <div className="w-14 h-14 flex items-center justify-center border border-border group-hover:border-foreground/40 group-hover:scale-110 mb-6 transition-all duration-500">
                  <cat.icon className="w-6 h-6 text-muted-foreground group-hover:text-foreground transition-colors duration-500" />
                </div>
                <h3 className="font-display text-xl text-foreground mb-3 group-hover:tracking-wider transition-all duration-500">
                  {cat.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed group-hover:text-foreground/70 transition-colors duration-500 mb-4">
                  {cat.description}
                </p>
                <span className="mt-auto inline-flex items-center gap-2 text-xs tracking-wide-elegant uppercase text-foreground/70 group-hover:text-foreground transition-colors">
                  Learn More <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
